import React, {FC, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'
import {useChat, OpenChatTarget} from '@containers/chat/ChatProvider'
import {getConversations} from '@containers/chat/api/api'
import {ChatConversation, getMessageTypeMeta} from '@containers/chat/types'
import DriverChatThread from '@containers/chat/thread/DriverChatThread'

interface Props {
    initialTarget: OpenChatTarget | null
    onClose: () => void
}

type Tab = 'unread' | 'all'

const preview = (text?: string): string => {
    if (!text) {
        return ''
    }
    return text.length > 50 ? `${text.slice(0, 50)}…` : text
}

/**
 * Global chat modal: an "unread" tab (grouped by route) and an "all chats" tab,
 * navigation into a driver thread and a back button (ported from the legacy GlobalChat modal).
 */
const ChatModal: FC<Props> = ({initialTarget, onClose}) => {
    const {groups, openChat, refresh, notificationsEnabled, setNotificationsEnabled} = useChat()
    const [tab, setTab] = useState<Tab>('unread')
    const [target, setTarget] = useState<OpenChatTarget | null>(initialTarget)
    const [conversations, setConversations] = useState<ChatConversation[]>([])
    const [loadingAll, setLoadingAll] = useState(false)

    useEffect(() => setTarget(initialTarget), [initialTarget])

    // The "all chats" list is fetched only when that tab is opened.
    useEffect(() => {
        if (tab !== 'all' || target) {
            return
        }
        setLoadingAll(true)
        getConversations(50)
            .then((res) => setConversations(res?.chats ?? []))
            .catch(() => setConversations([]))
            .finally(() => setLoadingAll(false))
    }, [tab, target])

    const back = () => {
        setTarget(null)
        refresh()
    }

    return createPortal(
        <div className="vs-chat" onClick={onClose}>
            <div className="vs-chat__box" onClick={(e) => e.stopPropagation()}>
                <div className="vs-chat__head">
                    {target && (
                        <button type="button" className="vs-chat__back" onClick={back} aria-label="Назад">←</button>
                    )}
                    <div className="vs-chat__title">
                        {target ? (target.routeName || target.driverName || 'Чат') : 'Чати'}
                    </div>
                    <button
                        type="button"
                        className={`vs-chat__notify${notificationsEnabled ? '' : ' vs-chat__notify--off'}`}
                        onClick={() => setNotificationsEnabled(!notificationsEnabled)}
                        aria-pressed={notificationsEnabled}
                        title={notificationsEnabled
                            ? 'Сповіщення про повідомлення увімкнені (звук і спливаючі вікна). Натисніть, щоб вимкнути'
                            : 'Сповіщення про повідомлення вимкнені. Натисніть, щоб увімкнути'}
                    >
                        {notificationsEnabled ? '🔔' : '🔕'}
                    </button>
                    <button type="button" className="vs-chat__close" onClick={onClose} aria-label="Закрити">×</button>
                </div>

                {target ? (
                    <DriverChatThread
                        driverId={target.driverId}
                        driverName={target.driverName}
                        plate={target.plate}
                    />
                ) : (
                    <>
                        <div className="vs-chat__tabs">
                            <button
                                type="button"
                                className={`vs-chat__tab${tab === 'unread' ? ' vs-chat__tab--active' : ''}`}
                                onClick={() => setTab('unread')}
                            >
                                💬 Непрочитані
                            </button>
                            <button
                                type="button"
                                className={`vs-chat__tab${tab === 'all' ? ' vs-chat__tab--active' : ''}`}
                                onClick={() => setTab('all')}
                            >
                                📋 Всі чати
                            </button>
                        </div>

                        <div className="vs-chat__panel">
                            {tab === 'unread' ? (
                                groups.length === 0 ? (
                                    <div className="vs-chat__empty">Непрочитаних повідомлень немає</div>
                                ) : (
                                    groups.map((group) => (
                                        <div key={group.routeId ?? group.routeName} className="vs-chat__group">
                                            <div className="vs-chat__groupname">
                                                {group.routeName || `Маршрут #${group.routeId}`}
                                            </div>
                                            {group.drivers.map((d) => {
                                                const meta = getMessageTypeMeta(d.lastMessage?.messageType ?? d.messageType)
                                                return (
                                                    <div
                                                        key={d.driverId}
                                                        className="vs-chat__item"
                                                        onClick={() => openChat({
                                                            driverId: d.driverId,
                                                            driverName: d.driverName,
                                                            plate: d.licensePlate,
                                                            routeName: group.routeName,
                                                        })}
                                                    >
                                                        <div className="vs-chat__itembody">
                                                            <div className="vs-chat__name">
                                                                {meta.icon && <span>{meta.icon} </span>}
                                                                {d.driverName || `Водій #${d.driverId}`}
                                                            </div>
                                                            {d.licensePlate && (
                                                                <div className="vs-chat__plate">{d.licensePlate}</div>
                                                            )}
                                                            {d.lastMessage?.message && (
                                                                <div className="vs-chat__preview">
                                                                    {preview(d.lastMessage.message)}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {(d.unreadCount ?? 0) > 0 && (
                                                            <span className="vs-chat__badge">{d.unreadCount}</span>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    ))
                                )
                            ) : loadingAll ? (
                                <div className="vs-chat__empty">Завантаження…</div>
                            ) : conversations.length === 0 ? (
                                <div className="vs-chat__empty">Діалогів ще немає</div>
                            ) : (
                                conversations.map((c) => (
                                    <div
                                        key={c.driverId}
                                        className="vs-chat__item"
                                        onClick={() => openChat({
                                            driverId: c.driverId,
                                            driverName: c.driverName,
                                            plate: c.licensePlate,
                                            routeName: c.routeName,
                                        })}
                                    >
                                        <div className="vs-chat__itembody">
                                            <div className="vs-chat__name">{c.driverName || `Водій #${c.driverId}`}</div>
                                            {c.routeName && <div className="vs-chat__plate">{c.routeName}</div>}
                                            {c.lastMessage?.message && (
                                                <div className="vs-chat__preview">{preview(c.lastMessage.message)}</div>
                                            )}
                                        </div>
                                        {(c.unreadCount ?? 0) > 0 && (
                                            <span className="vs-chat__badge">{c.unreadCount}</span>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body,
    )
}

export default ChatModal